import { useState } from 'react';

import { ComposerDecision } from '../composer-gate-context';
import { DecisionFooter } from './decision-footer';

/**
 * The launch decision for a draft ad that already carries its approval token.
 *
 * `createDraftAd` mints the launch token with the draft, so the owner's next
 * "yes" is the approval. Without a control for that yes, Claire told owners to
 * "confirm on the card above" over a preview card that had no button, and the
 * only way through was guessing the right words. The preview itself stays in
 * the transcript; only the decision moves into the composer.
 *
 * Like every composer decision, answering SENDS A MESSAGE: the model holds the
 * token and makes the launch call in the next turn.
 */
export function DraftAdLaunchDecision({
  id,
  adName,
  budgetDisplay,
  onSendMessage,
}: {
  /** The tool call this renders from — a stable id for gate registration. */
  id: string;
  adName: string;
  budgetDisplay?: string;
  onSendMessage: (text: string) => void;
}) {
  const [answered, setAnswered] = useState(false);
  if (answered) return null;

  const sentence = `Launch "${adName}"?`;
  const answer = (text: string) => {
    setAnswered(true);
    onSendMessage(text);
  };
  const onAccept = () => answer('Yes, launch the ad.');
  const onReject = () => answer("Don't launch it yet.");

  return (
    <ComposerDecision
      id={id}
      gate={{
        sentence,
        onAccept,
        onReject,
        acceptLabel: 'Launch',
        rejectLabel: 'Not yet',
      }}
    >
      <div
        data-testid="claire-card-draft-ad-launch"
        className="w-full rounded-lg border bg-card p-4 sm:max-w-lg"
      >
        <p className="text-sm font-medium">{sentence}</p>
        <p className="mt-1 text-xs text-muted-foreground">
          {budgetDisplay
            ? `Budget ${budgetDisplay}. Nothing is spent until you launch.`
            : 'Nothing is spent until you launch.'}
        </p>
        <div className="mt-3">
          <DecisionFooter
            hideSentence
            sentence={sentence}
            onAccept={onAccept}
            onReject={onReject}
            acceptLabel="Launch"
            rejectLabel="Not yet"
          />
        </div>
      </div>
    </ComposerDecision>
  );
}
